import {
  Box,
  Button,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Tag,
  Text,
  Badge,
  useDisclosure,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { AiOutlineSearch } from "react-icons/ai";
import { BiFilter } from "react-icons/bi";
import { FaEye } from "react-icons/fa";
import dayjs from "dayjs";
import AdminDataTable from "components/AdminDataTable/AdminDataTable";
import Paginator from "components/Paginator";
import { useQueryWrapper } from "services/apiHelper";
import { scriptsRequest } from "services/request";
import {
  QUERY_KEYS,
  getEditRequestFieldGroups,
  type EditRequestType,
  type ScriptType,
} from "utils/constants";
import EditRequestDiffModal from "./EditRequestDiffModal";

type StatusFilterType = "pending" | "approved" | "discarded" | "all";

interface EditRequestsTableProps {
  adminScripts?: ScriptType[];
}

const getNonEmptyString = (value: any) =>
  typeof value === "string" && value.trim() ? value.trim() : undefined;

const getComposerName = (script: any) =>
  getNonEmptyString(script?.scriptComposer?.name) ?? getNonEmptyString(script?.composer) ?? "-";

const getRequesterName = (request: any, script: any) =>
  getNonEmptyString(request?.user?.userName) ??
  getNonEmptyString(request?.user?.email) ??
  getNonEmptyString(request?.requestedBy?.userName) ??
  getNonEmptyString(request?.requestedBy?.email) ??
  getNonEmptyString(request?.requestedBy) ??
  getNonEmptyString(script?.user?.userName) ??
  getNonEmptyString(script?.user?.email) ??
  "-";

const EditRequestsTable = ({ adminScripts = [] }: EditRequestsTableProps) => {
  const [statusFilter, setStatusFilter] = useState<StatusFilterType>("pending");
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [postsPerPage, setPostsPerPage] = useState(10);
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
  const [selectedScript, setSelectedScript] = useState<ScriptType | null>(null);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const queryUrl = useMemo(() => {
    if (statusFilter === "all") {
      return `${scriptsRequest.EDIT_REQUESTS}?status=all`;
    }
    return `${scriptsRequest.EDIT_REQUESTS}?status=${statusFilter}`;
  }, [statusFilter]);

  const queryKey = `${QUERY_KEYS.EDIT_REQUESTS}_${statusFilter}`;

  const { data: rawData, isLoading, refetch } = useQueryWrapper(queryKey, queryUrl);

  const editRequests: EditRequestType[] = useMemo(() => {
    if (!rawData) return [];
    if (Array.isArray(rawData)) return rawData;
    if (Array.isArray(rawData?.data)) return rawData.data;
    return [];
  }, [rawData]);

  const adminScriptById = useMemo(
    () =>
      adminScripts.reduce<Record<string, ScriptType>>((acc, script) => {
        if (script?.id) {
          acc[script.id] = script;
        }
        return acc;
      }, {}),
    [adminScripts],
  );

  const getRequestScript = (request: EditRequestType) => {
    const requestScript = request.script;
    return (
      adminScriptById[request.scriptId] ?? adminScriptById[requestScript?.id ?? ""] ?? requestScript
    );
  };

  const filteredData = useMemo(() => {
    if (!searchText.trim()) return editRequests;
    const lower = searchText.toLowerCase();
    return editRequests.filter((item) => {
      const script = getRequestScript(item);
      const scriptName = script?.name?.toLowerCase() ?? "";
      const composerName = getComposerName(script).toLowerCase();
      const uploaderName = getRequesterName(item, script).toLowerCase();
      return (
        scriptName.includes(lower) || composerName.includes(lower) || uploaderName.includes(lower)
      );
    });
  }, [editRequests, getRequestScript, searchText]);

  useEffect(() => {
    setCurrentPage(0);
  }, [statusFilter, searchText]);

  const lastPostIndex = (currentPage + 1) * postsPerPage;
  const firstPostIndex = lastPostIndex - postsPerPage;
  const currentRequests = filteredData.slice(firstPostIndex, lastPostIndex);
  const pageCount = Math.max(1, Math.ceil(filteredData.length / postsPerPage));

  const columns = useMemo(
    () => [
      {
        Header: "Script Name",
        Cell: ({ cell }: any) => {
          const req: EditRequestType = cell.row.original;
          const script = getRequestScript(req);
          return (
            <Box>
              <Text fontWeight="medium" color="#2b3b4c">
                {script?.name ?? "Untitled Script"}
              </Text>
              {script?.slug && (
                <Text
                  as="span"
                  fontSize="xs"
                  color="#3182ce"
                  cursor="pointer"
                  _hover={{ textDecoration: "underline" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(`/scripts/${script.slug}`, "_blank");
                  }}
                >
                  View live page ↗
                </Text>
              )}
            </Box>
          );
        },
      },
      {
        Header: "Composer",
        Cell: ({ cell }: any) => {
          const req: EditRequestType = cell.row.original;
          return <Text fontSize="sm">{getComposerName(getRequestScript(req))}</Text>;
        },
      },
      {
        Header: "Requested By",
        Cell: ({ cell }: any) => {
          const req: EditRequestType = cell.row.original;
          return <Text fontSize="sm">{getRequesterName(req, getRequestScript(req))}</Text>;
        },
      },
      {
        Header: "Requested On",
        Cell: ({ cell }: any) => {
          const req: EditRequestType = cell.row.original;
          return (
            <Text fontSize="sm">
              {req.createdAt ? dayjs(req.createdAt).format("MMM D YYYY") : "-"}
            </Text>
          );
        },
      },
      {
        Header: "Changes",
        Cell: ({ cell }: any) => {
          const req: EditRequestType = cell.row.original;
          const groups = getEditRequestFieldGroups(req.proposedChanges);
          const count = groups.length;
          const resolvedCount = groups.filter((group) =>
            Boolean(req.resolvedFields?.[group.key]),
          ).length;
          const isFullyResolved =
            Boolean(req.fullyResolved) || (count > 0 && resolvedCount >= count);

          return (
            <Flex direction="column" gap={1} align="flex-start">
              <Badge colorScheme="purple" borderRadius="full" px={2} py={0.5}>
                {count} {count === 1 ? "group" : "groups"}
              </Badge>
              {resolvedCount > 0 && (
                <Badge
                  colorScheme={isFullyResolved ? "green" : "orange"}
                  fontSize="2xs"
                  borderRadius="full"
                  px={1.5}
                >
                  {isFullyResolved ? "All resolved" : `${resolvedCount}/${count} resolved`}
                </Badge>
              )}
            </Flex>
          );
        },
      },
      {
        Header: "Status",
        Cell: ({ cell }: any) => {
          const status = cell.row.original.status ?? "pending";
          return (
            <Tag
              borderRadius="full"
              size="sm"
              colorScheme={
                status === "approved" ? "green" : status === "discarded" ? "red" : "yellow"
              }
              textTransform="capitalize"
              px={3}
            >
              {status}
            </Tag>
          );
        },
      },
      {
        Header: "Action",
        Cell: ({ cell }: any) => {
          const req: EditRequestType = cell.row.original;
          return (
            <Button
              size="sm"
              colorScheme="blue"
              variant="outline"
              leftIcon={<FaEye />}
              onClick={() => {
                setSelectedRequestId(req.id);
                setSelectedScript(getRequestScript(req) ?? null);
                onOpen();
              }}
            >
              Review Changes
            </Button>
          );
        },
      },
    ],
    [getRequestScript, onOpen],
  );

  return (
    <Box>
      <Box p={"0 10px"} mt="2">
        <InputGroup mb={4} w="100%">
          <InputLeftElement pointerEvents="none">
            <AiOutlineSearch color="gray.300" />
          </InputLeftElement>
          <Input
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
            }}
            type="search"
            placeholder="Search edit requests by script name, composer, or user"
          />
        </InputGroup>
      </Box>

      <Flex justify="space-between" align="center" px="10px" mb={4}>
        <Box>
          <Menu>
            <MenuButton
              as={Button}
              aria-label="Filter edit requests by status"
              leftIcon={<BiFilter />}
              variant="outline"
              fontWeight="normal"
              size="sm"
            >
              Status: <span style={{ textTransform: "capitalize" }}>{statusFilter}</span>
            </MenuButton>
            <MenuList minWidth="200px" zIndex={14}>
              <MenuOptionGroup
                onChange={(val: any) => {
                  setStatusFilter(val as StatusFilterType);
                }}
                defaultValue={statusFilter}
                title="Status Filter"
                type="radio"
              >
                <MenuItemOption value="pending">Pending</MenuItemOption>
                <MenuItemOption value="approved">Approved</MenuItemOption>
                <MenuItemOption value="discarded">Discarded</MenuItemOption>
                <MenuItemOption value="all">All</MenuItemOption>
              </MenuOptionGroup>
            </MenuList>
          </Menu>
        </Box>

        <Text fontSize="xs" color="gray.500">
          Showing {filteredData.length} {filteredData.length === 1 ? "request" : "requests"}
        </Text>
      </Flex>

      <Box p="4">
        <AdminDataTable
          ariaLabel="Edit requests"
          columns={columns}
          data={currentRequests}
          loading={isLoading}
          showRowNumbers
          startIndex={+postsPerPage * currentPage + 1}
        />
      </Box>

      <Box px="4">
        <Paginator
          pageCount={pageCount}
          onPageChange={(page) => {
            setCurrentPage(page.selected);
          }}
          pageNo={currentPage}
          pageSize={postsPerPage}
          onSizeChange={(size) => {
            setPostsPerPage(+size);
            setCurrentPage(0);
          }}
        />
      </Box>

      <EditRequestDiffModal
        isOpen={isOpen}
        onClose={() => {
          setSelectedRequestId(null);
          setSelectedScript(null);
          onClose();
        }}
        requestId={selectedRequestId}
        scriptFallback={selectedScript}
        onSuccess={() => {
          void refetch();
        }}
      />
    </Box>
  );
};

export default EditRequestsTable;
